import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import AdminNav from "../components/AdminNav";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;

const Stat = ({ label, value, accent = false, testid }) => (
  <div data-testid={testid} className="bg-[#050505]/90 border border-[#d4af37]/30 p-5 sm:p-6">
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{label}</div>
    <div className={`font-display text-3xl sm:text-4xl ${accent ? "text-[#d4af37]" : "text-white"}`}>{value}</div>
  </div>
);

const PeriodBlock = ({ title, data, testid }) => (
  <div data-testid={testid} className="bg-[#050505]/90 border border-zinc-800 p-5">
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// {title}</div>
    <div className="grid grid-cols-2 gap-3 text-sm font-mono">
      <div>
        <div className="text-zinc-500 text-[9px] tracking-[0.2em] uppercase mb-1">Orders</div>
        <div className="text-xl">{data?.orders ?? 0}</div>
      </div>
      <div>
        <div className="text-zinc-500 text-[9px] tracking-[0.2em] uppercase mb-1">Gross (inc VAT)</div>
        <div className="text-xl text-[#d4af37]">{fmt(data?.gross)}</div>
      </div>
      <div>
        <div className="text-zinc-500 text-[9px] tracking-[0.2em] uppercase mb-1">Net (ex VAT)</div>
        <div className="text-lg">{fmt(data?.net)}</div>
      </div>
      <div>
        <div className="text-zinc-500 text-[9px] tracking-[0.2em] uppercase mb-1">VAT</div>
        <div className="text-lg text-zinc-400">{fmt(data?.vat)}</div>
      </div>
    </div>
  </div>
);

const AdminFinance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/finance")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load finance data"))
      .finally(() => setLoading(false));
  }, []);

  const inv = data?.inventory;
  const rev = data?.revenue;

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="admin" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <AdminNav />
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// FINANCE</div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#d4af37]">Money Flow</h1>
            <p className="text-zinc-500 text-xs mt-2">Inventory value · production cost · realised revenue · weekly digest emails</p>
          </div>
          <div className="flex gap-2 flex-wrap">
          <button
            data-testid="refresh-finance"
            onClick={() => {
              setLoading(true);
              api.get("/admin/finance").then((r) => setData(r.data)).finally(() => setLoading(false));
            }}
            className="btn-raw"
          >
            ↻ REFRESH
          </button>
          <button
            data-testid="send-digest-btn"
            onClick={async () => {
              try {
                const r = await api.post("/admin/digest/send");
                if (r.data?.sent_to) {
                  toast.success(`Digest emailed to ${r.data.sent_to}`);
                } else if (r.data?.error) {
                  toast.error(`Sent but Resend said: ${r.data.error.slice(0, 120)}`);
                } else {
                  toast.info("Digest skipped — email not configured yet");
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed");
              }
            }}
            className="btn-raw btn-raw-primary"
          >
            ✉ SEND WEEKLY DIGEST NOW
          </button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING...</div>
        ) : !data ? (
          <div className="py-20 text-center text-zinc-500">No data yet.</div>
        ) : (
          <>
            {/* Inventory summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10" data-testid="inventory-summary">
              <Stat testid="stat-total-skus" label="Total SKUs" value={inv.total_sku} />
              <Stat testid="stat-total-units" label="Units in Stock" value={inv.total_units_in_stock} />
              <Stat testid="stat-inventory-value" label="Shop Value" value={fmt(inv.inventory_value)} accent />
              <Stat testid="stat-potential-profit" label="Potential Profit" value={fmt(inv.potential_profit)} accent />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-12" data-testid="cost-summary">
              <Stat label="Total Production Cost" value={fmt(inv.inventory_cost)} />
              <Stat label="Gross Margin on Stock" value={inv.inventory_value > 0 ? `${Math.round(((inv.inventory_value - inv.inventory_cost) / inv.inventory_value) * 100)}%` : "—"} accent />
            </div>

            {/* Revenue by period */}
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// SALES / REVENUE</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="revenue-periods">
              <PeriodBlock testid="rev-today" title="Today" data={rev.today} />
              <PeriodBlock testid="rev-week" title="This Week" data={rev.week} />
              <PeriodBlock testid="rev-month" title="This Month" data={rev.month} />
              <PeriodBlock testid="rev-year" title="This Year" data={rev.year} />
            </div>
            <div className="grid grid-cols-1 gap-3 mb-12">
              <PeriodBlock testid="rev-alltime" title="All Time" data={rev.all_time} />
            </div>

            {/* Per-product table */}
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// INVENTORY BREAKDOWN</div>
            <div className="overflow-x-auto border border-zinc-800" data-testid="product-finance-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0a0a0a]">
                    <th className="p-3 text-left text-[10px] tracking-[0.2em] uppercase text-zinc-500">Product</th>
                    <th className="p-3 text-right text-[10px] tracking-[0.2em] uppercase text-zinc-500">Stock</th>
                    <th className="p-3 text-right text-[10px] tracking-[0.2em] uppercase text-zinc-500">Price</th>
                    <th className="p-3 text-right text-[10px] tracking-[0.2em] uppercase text-zinc-500">Cost</th>
                    <th className="p-3 text-right text-[10px] tracking-[0.2em] uppercase text-zinc-500">Value</th>
                    <th className="p-3 text-right text-[10px] tracking-[0.2em] uppercase text-zinc-500">Potential Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.per_product.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-800/50" data-testid={`row-${p.id}`}>
                      <td className="p-3">
                        <div className="text-white">{p.name || "(untitled)"}</div>
                        <div className="text-zinc-500 text-[10px] tracking-[0.2em] uppercase">
                          {p.clothing_type} · {p.gender?.toUpperCase()} {p.published ? "" : "· DRAFT"}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{p.stock}</td>
                      <td className="p-3 text-right font-mono">{fmt(p.price)}</td>
                      <td className="p-3 text-right font-mono text-zinc-400">{fmt(p.cost_to_make)}</td>
                      <td className="p-3 text-right font-mono text-[#d4af37]">{fmt(p.inventory_value)}</td>
                      <td className="p-3 text-right font-mono text-[#d4af37]">{fmt(p.potential_profit)}</td>
                    </tr>
                  ))}
                  {inv.per_product.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-zinc-600 text-xs tracking-[0.3em] uppercase">NO PRODUCTS YET</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFinance;
